'use client';

import { Room } from 'livekit-client';
import {
  RECORDING_EVENT_TYPE,
  type RecordingAction,
  type RecordingEventPayload,
} from '@/lib/recording-event-types';

export type { RecordingAction, RecordingEventPayload };
export { RECORDING_EVENT_TYPE };

export function publishRecordingEvent(
  room: Room | null | undefined,
  action: RecordingAction,
  extra?: { queuePosition?: number },
) {
  if (!room?.localParticipant) return;

  const payload: RecordingEventPayload = {
    type: RECORDING_EVENT_TYPE,
    action,
    actorIdentity: room.localParticipant.identity,
    actorName: room.localParticipant.name ?? null,
    queuePosition: extra?.queuePosition,
    timestamp: Date.now(),
  };

  room.localParticipant
    .publishData(new TextEncoder().encode(JSON.stringify(payload)), {
      reliable: true,
    })
    .catch((error) => {
      console.error('Erro ao publicar evento de gravação:', error);
    });
}

const VALID_ACTIONS: RecordingAction[] = [
  'preparing',
  'starting',
  'started',
  'stopped',
  'failed',
  'cancelled',
];

export function parseRecordingEvent(
  payload: Uint8Array,
  fallbackActorName?: string | null,
): {
  action: RecordingAction;
  actorName: string;
  actorIdentity?: string | null;
  queuePosition?: number;
  message?: string;
} | null {
  try {
    const decoded = new TextDecoder().decode(payload);
    const data = JSON.parse(decoded) as Partial<RecordingEventPayload>;

    if (data?.type !== RECORDING_EVENT_TYPE) return null;
    if (!data.action || !VALID_ACTIONS.includes(data.action)) return null;

    const actorName = (data.actorName || fallbackActorName)?.trim();

    return {
      action: data.action as RecordingAction,
      actorName: actorName && actorName.length > 0 ? actorName : 'Alguém',
      actorIdentity: data.actorIdentity ?? null,
      queuePosition: data.queuePosition,
      message: typeof data.message === 'string' ? data.message : undefined,
    };
  } catch (error) {
    console.error('Erro ao interpretar evento de gravação:', error);
    return null;
  }
}

export function recordingActionToWindowEvent(action: RecordingAction): string {
  switch (action) {
    case 'preparing':
      return 'recording:preparing';
    case 'starting':
      return 'recording:starting';
    case 'started':
      return 'recording:start';
    case 'stopped':
    case 'cancelled':
    case 'failed':
      return 'recording:stop';
    default:
      return 'recording:stop';
  }
}
